import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from './context';
import { ErrorBoundary } from './components/common';

// Regular imports
import Login from './pages/Login';
import Callback from './pages/Callback';
import Home from './pages/Home';
import Search from './pages/Search';
import Playlists from './pages/Playlists';
import Settings from './pages/Settings';

// Lazy-loaded components
const PlaylistDetail = lazy(() => import('./pages/PlaylistDetail'));
const TrackDetail = lazy(() => import('./pages/TrackDetail'));
const AIChat = lazy(() => import('./pages/AIChat'));
const PlaylistAgent = lazy(() => import('./pages/PlaylistAgent'));
const DJPlaylist = lazy(() => import('./pages/DJPlaylist'));

// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page with the return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Wait for auth to be checked before rendering
    if (!isLoading) {
      setIsReady(true);
    }
  }, [isLoading]);

  if (!isReady) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/callback" element={<Callback />} />
      
      {/* Protected routes */}
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route 
        path="/home" 
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/search" 
        element={
          <ProtectedRoute>
            <Search />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/playlists" 
        element={
          <ProtectedRoute>
            <Playlists />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/playlist/:playlistId" 
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <Suspense fallback={
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                  <CircularProgress />
                </Box>
              }>
                <PlaylistDetail />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/track/:trackId" 
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <Suspense fallback={
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                  <CircularProgress />
                </Box>
              }>
                <TrackDetail />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ai" 
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <Suspense fallback={
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                  <CircularProgress />
                </Box>
              }>
                <AIChat />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/playlist-agent" 
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <Suspense fallback={
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                  <CircularProgress />
                </Box>
              }>
                <PlaylistAgent />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/dj-playlist" 
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <Suspense fallback={
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                  <CircularProgress />
                </Box>
              }>
                <DJPlaylist />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } 
      />
      
      {/* Fallback route */}
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
};

export default App;
